import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Gamepad2, ScrollText, ClipboardCheck, Sparkles, Star } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { Carlos, Ana, Mascot, GonzalezFamily } from "@/components/characters/Characters";
import { CanalesLogo } from "@/components/CanalesLogo";
import ShareAppButton from "@/components/ShareAppButton";
import { useLang } from "@/context/LangContext";
import { HOME } from "@/constants/testIds";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 ${className}`}>{children}</section>
);

const Feature = ({ Icon, title, desc, to, color = "#005BBB" }) => (
  <Link to={to} className="brutal p-6 hover:-translate-y-1 transition-transform block bg-white">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border-2 border-black" style={{ background: color }}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="mt-4 font-display font-black text-xl">{title}</h3>
    <p className="mt-1 font-body text-neutral-700">{desc}</p>
  </Link>
);

export default function Landing() {
  const { t, lang } = useLang();

  return (
    <div className="min-h-screen bg-halftone">
      <Navbar />

      {/* HERO */}
      <Section className="!py-16 sm:!py-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7 relative"
          >
            <CanalesLogo width={280} className="mb-4 drop-shadow-lg" />
            <span className="inline-flex items-center gap-2 bg-canales-yellow border-2 border-black rounded-full px-3 py-1 font-display font-bold text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4" /> {t("tagline")}
            </span>
            <h1 className="mt-4 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              <span className="inline-block bg-canales-blue text-white px-3 py-1 rounded-lg border-4 border-black shadow-comic-lg">
                CANALES
              </span>{" "}
              <span className="inline-block">Furniture</span>
              <br />
              <span className="text-canales-red">
                {lang === "es" ? "¡Aprende y" : "Learn &"}
              </span>{" "}
              <span className="underline decoration-canales-yellow decoration-8 underline-offset-4">
                {lang === "es" ? "vende como pro!" : "sell like a pro!"}
              </span>
            </h1>
            <p className="mt-6 font-body text-lg sm:text-xl max-w-xl">
              {lang === "es"
                ? "Una revista tipo cómic + juegos + exámenes compartibles. Con Carlos, Ana y el sillón mascota. ¡Diseñada para vendedores Canales!"
                : "A comic-style magazine + games + shareable exams. With Carlos, Ana and our sofa mascot. Made for Canales sellers!"}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/magazine"
                data-testid={HOME.startBtn}
                className="brutal-btn bg-canales-red text-white px-6 py-3 text-lg inline-flex items-center gap-2"
              >
                <BookOpen className="w-5 h-5" /> {t("heroCTA")}
              </Link>
              <Link
                to="/onboarding"
                className="brutal-btn bg-canales-yellow text-black px-6 py-3 text-lg inline-flex items-center gap-2"
              >
                <ClipboardCheck className="w-5 h-5" /> {lang === "es" ? "Ruta de Onboarding" : "Onboarding Path"}
              </Link>
              <Link
                to="/login"
                className="brutal-btn bg-canales-blue text-white px-6 py-3 text-lg inline-flex items-center gap-2"
              >
                <ClipboardCheck className="w-5 h-5" /> {lang === "es" ? "Manager / Vendedor Login" : "Manager / Seller Login"}
              </Link>
              <Link
                to="/exams-info"
                className="brutal-btn bg-white text-black px-6 py-3 text-lg inline-flex items-center gap-2"
              >
                <ClipboardCheck className="w-5 h-5" /> {t("exams")}
              </Link>
              <Link
                to="/ranking"
                className="brutal-btn bg-white text-black px-6 py-3 text-lg inline-flex items-center gap-2"
              >
                <Star className="w-5 h-5" /> {t("ranking")}
              </Link>
              <ShareAppButton size="lg" variant="yellow" />
            </div>

            {/* comic stars row */}
            <div className="mt-6 flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-canales-yellow text-black" />
              ))}
              <span className="ml-2 font-display font-bold">
                {lang === "es" ? "Estilo Vendedor Estrella" : "Star Seller style"}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5 relative"
          >
            <div className="brutal p-6 bg-white relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="brutal-sm p-3 bg-canales-yellow flex items-center justify-center">
                  <Carlos size={110} waving />
                </div>
                <div className="brutal-sm p-3 bg-canales-blue flex items-center justify-center">
                  <Ana size={110} />
                </div>
                <div className="brutal-sm p-3 bg-white col-span-2 flex items-center justify-center">
                  <Mascot size={140} />
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-canales-red text-white font-display font-black text-lg px-4 py-2 rounded-full border-4 border-black rotate-6 shadow-comic-lg">
                4K COMIC!
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* FEATURES */}
      <Section>
        <h2 className="font-display font-black text-3xl sm:text-4xl mb-6">
          {lang === "es" ? "¿Qué vas a aprender?" : "What will you learn?"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature Icon={BookOpen} title={t("chapters")}
            desc={lang === "es" ? "10 capítulos ilustrados con Carlos y Ana" : "10 illustrated chapters with Carlos & Ana"}
            to="/magazine" color="#005BBB" />
          <Feature Icon={Gamepad2} title={t("games")}
            desc={lang === "es" ? "Mini-juegos: ordena, encuentra el error, F.A.B." : "Mini-games: order, find the mistake, F.A.B."}
            to="/games" color="#E3000F" />
          <Feature Icon={ScrollText} title={t("stories")}
            desc={lang === "es" ? "20 historias con final según tu elección" : "20 stories that branch on your choice"}
            to="/stories" color="#FFB800" />
          <Feature Icon={ClipboardCheck} title={t("exams")}
            desc={lang === "es" ? "Exámenes compartibles por link con QR" : "Shareable exam links with QR codes"}
            to="/exams-info" color="#0B0B14" />
        </div>
      </Section>

      {/* FAMILY */}
      <Section>
        <div className="brutal p-8 bg-canales-cream">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="font-display font-black text-3xl sm:text-4xl">
                {lang === "es" ? "Los González" : "The González family"}
              </h2>
              <p className="mt-2 font-body text-lg">
                {lang === "es"
                  ? "Nuestros clientes protagonistas. Con ellos vas a practicar cada paso, desde el saludo hasta el follow-up."
                  : "Our star customers. You'll practice every step with them, from the greeting to the follow-up."}
              </p>
            </div>
            <div className="flex justify-center">
              <GonzalezFamily size={340} />
            </div>
          </div>
        </div>
      </Section>

      <footer className="border-t-4 border-black bg-canales-blue text-white py-6 text-center font-display">
        <div className="text-sm">
          © {new Date().getFullYear()} Canales Furniture · Revista Educativa Interactiva
        </div>
      </footer>
    </div>
  );
}
